import React from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import CardioSelectDialog from "./CardioSelectDialog";
import { nanoid } from "nanoid";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import RecentHistory from "../../../../components/molecules/RecentHistory";
import { getHistoryString } from "../../../../components/LibraryFunctions";
import useEvents from "../../../../hooks/useEvents";
import { getCardioSessionsPerWeek } from "../../../../utils/cardioGoalDisplay";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NewColors from "../../../../themes/NewColors";

const CardioGoalRow = ({ cardioGoal, onEdit, onDelete, onActivate }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      type: "menu",
      menuName: "Set Goal as Active",
      menuFunction: () => {
        if (onActivate) {
          onActivate(cardioGoal);
        }
      },
      hidden: cardioGoal.active, // Hide if already active
    },
    {
      type: "menu",
      menuName: "Edit Cardio Goal",
      menuFunction: () => {
        if (onEdit) {
          onEdit(cardioGoal);
        }
      },
    },
    {
      type: "menu",
      menuName: "Delete Cardio Goal",
      menuFunction: () => {
        if (onDelete) {
          onDelete(cardioGoal);
        }
      },
    },
  ];

  return (
    <Box
      sx={{
        padding: 2,
        borderRadius: 1,
        mb: 1,
        display: "flex",
        alignItems: "center",
        backgroundColor: cardioGoal.active
          ? "rgba(46, 125, 50, 0.08)"
          : "white",
        border: cardioGoal.active
          ? "1px solid rgba(46, 125, 50, 0.3)"
          : "1px solid rgba(0, 0, 0, 0.12)",
        "&:hover": {
          backgroundColor: cardioGoal.active
            ? "rgba(46, 125, 50, 0.12)"
            : "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Box sx={{ display: "flex", flex: 1 }}>
        <Box sx={{ width: "30%", mr: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {cardioGoal.name}
          </Typography>
          {cardioGoal.active && (
            <Chip
              label="Active"
              color="success"
              size="small"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", flex: 1 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ border: "none", pl: 0, py: 0.5 }}>
                  Sessions
                </TableCell>
                <TableCell sx={{ border: "none", py: 0.5 }}>
                  {getCardioSessionsPerWeek(cardioGoal) ?? 0}
                </TableCell>
                <TableCell sx={{ border: "none", pl: 2, py: 0.5 }}>
                  Duration
                </TableCell>
                <TableCell sx={{ border: "none", py: 0.5 }}>
                  {cardioGoal.duration || "30 min"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ border: "none", pl: 0, py: 0.5 }}>
                  Intensity
                </TableCell>
                <TableCell sx={{ border: "none", py: 0.5 }}>
                  {cardioGoal.intensity || "Medium"}
                </TableCell>
                <TableCell sx={{ border: "none", pl: 2, py: 0.5 }}>
                  Target HR
                </TableCell>
                <TableCell sx={{ border: "none", py: 0.5 }}>
                  {cardioGoal.targetHR || "120-140"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Box sx={{ ml: 2 }}>
        <MoreVertIcon onClick={handleClick} sx={{ cursor: "pointer" }} />
      </Box>

      <Menu
        id="cardio-goal-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {menuItems
          .filter((item) => !item.hidden)
          .map((menuItem, index) => {
            if (menuItem.type === "menu") {
              return (
                <MenuItem
                  key={menuItem.menuName}
                  onClick={() => {
                    setAnchorEl(null);
                    if (menuItem.menuFunction) {
                      menuItem.menuFunction(cardioGoal);
                    }
                  }}
                >
                  {menuItem.menuName}
                </MenuItem>
              );
            } else {
              return <Divider key={index} />;
            }
          })}
      </Menu>
    </Box>
  );
};

function CardioGoals({ style, onEdit, onDelete, onActivate, client, onClose }) {
  const confirm = useConfirm();
  const [cardioSelectOpen, setCardioSelectOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  // editingGoal tracks if the cardio dialogue is for editing or adding a goal
  const [editingGoal, setEditingGoal] = React.useState(true);

  const selectedCardioGoal = React.useRef(null);
  const events = useEvents();

  function formatDate(timestamp) {
    if (!timestamp) return "";
    return moment(timestamp.toDate()).format("MMM D, YYYY [at] h:mm A");
  }

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: NewColors.sectionBackground,
        borderRadius: 2,
        overflow: "hidden",
        ...style,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          pb: 2,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Box display="flex" alignItems="center">
          <Box sx={{ mr: 2 }}>
            <FitnessCenterIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="600">
              Cardio Goals
            </Typography>
            {client.cardioGoalChanged && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Last updated: {formatDate(client.cardioGoalChanged)}
              </Typography>
            )}
          </Box>
        </Box>

        {onClose && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ px: 4, py: 3 }}>
        {/* Goals List */}
        <Box sx={{ maxHeight: "400px", overflow: "auto", mb: 4 }}>
          {client.cardioGoals && client.cardioGoals.length > 0 ? (
            client.cardioGoals.map((cardioGoal) => (
              <CardioGoalRow
                key={cardioGoal.id}
                cardioGoal={cardioGoal}
                onEdit={(cardioGoal) => {
                  selectedCardioGoal.current = cardioGoal;
                  setEditingGoal(true);
                  setCardioSelectOpen(true);
                }}
                onDelete={(cardioGoal) => {
                  confirm({
                    title: "Delete the cardio goal?",
                    description:
                      "Deleting this cardio goal cannot be undone. Continue?",
                  }).then(async () => {
                    if (onDelete) {
                      onDelete(cardioGoal);
                    }
                  }).catch(() => {
                    // User cancelled the confirmation - no action needed
                  });
                }}
                onActivate={(cardioGoal) => {
                  if (onActivate) {
                    onActivate(cardioGoal);
                  }
                }}
              />
            ))
          ) : (
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "grey.50",
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: "grey.300",
                mb: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No cardio goals set. Click &quot;Add Cardio Goal&quot; to create
                one.
              </Typography>
            </Paper>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              selectedCardioGoal.current = {
                id: nanoid(),
                name: "New Cardio Goal",
                sessions: 3,
                duration: "30 minutes",
                intensity: "Medium",
                targetHR: "120-140",
              };
              setEditingGoal(false);
              setCardioSelectOpen(true);
            }}
          >
            Add Cardio Goal
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              if (!historyOpen) {
                const logEvent = {
                  buttonName: "Display Cardio History",
                  pageName: "Clients",
                  tabName: client.uid,
                };
                events.logButtonClick(logEvent);
              }
              setHistoryOpen(!historyOpen);
            }}
          >
            {getHistoryString(historyOpen)}
          </Button>
        </Stack>

        {/* History Section */}
        {historyOpen && (
          <Box sx={{ mt: 2 }}>
            <RecentHistory
              client={client}
              historyCollection={"cardioGoalHistory"}
            />
          </Box>
        )}
      </Box>

      {/* Cardio Selection Dialog */}
      {cardioSelectOpen && (
        <CardioSelectDialog
          client={client}
          open={cardioSelectOpen}
          cardioGoal={selectedCardioGoal.current}
          isEdit={editingGoal}
          onDialogClosed={(cardioGoal) => {
            setCardioSelectOpen(false);

            if (cardioGoal) {
              if (onEdit) {
                onEdit(cardioGoal);
              }
            }
          }}
        />
      )}
    </Paper>
  );
}

export default CardioGoals;
